import { memo, useCallback, useEffect } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import AvatarUpload from '@/components/atoms/Avatar/AvatarUpload/AvatarUpload';
import fs from '@/components/atoms/Fieldset';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { selectIsWebchatTeaserEnabled } from '@/redux/featureFlag/selectors';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { AccordionFieldsetProps, WebWidgetIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { errorMessage, trimOnBlur } from '@/util/validation/helpers';
import { integrationRulesWebWidget } from '@/util/validation/rules/integration-rules';

import LauncherOptions from './LauncherOptions';
import { fromTeaserBlock, teaserForSave } from './Teaser/serialize';
import Teaser from './Teaser/Teaser';
import { TeaserForm } from './Teaser/types';

type Form = {
  launcher_image_checkbox: boolean;
  launcher_type: 'bubble' | 'bubble-sparkle' | 'help' | 'feedback' | 'question';
  focus_trap: boolean;
  auto_open: boolean;
  welcome_message: string;
  bubble_image_url: string;
  teaser: TeaserForm;
};

const LauncherAppearance = ({
  type,
  integration,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, WebWidgetIntegration>) => {
  const { t } = useTranslation();
  const { expanded } = useSelector(selectAccordion);
  const isTeaserEnabled = useSelector(selectIsWebchatTeaserEnabled);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const { updateIntegration, updateStatus } = useIntegrations<WebWidgetIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );
  const dispatch = useDispatch();

  const formMethods = useForm<Form>({ mode: 'onSubmit' });

  const {
    reset,
    handleSubmit,
    formState: { errors, isDirty },
    trigger,
    control,
    setValue,
    setError,
    clearErrors,
  } = formMethods;

  const [launcherImageActive, launcherType, bubbleImageUrl] = useWatch({
    control,
    name: ['launcher_image_checkbox', 'launcher_type', 'bubble_image_url'],
  });

  useEffect(() => {
    const welcomeMessage = integration?.config?.welcome_message ?? '';
    const teaser = fromTeaserBlock(integration?.config?.teaser);
    reset({
      welcome_message: welcomeMessage,
      launcher_image_checkbox: !!integration?.config?.bubble_image,
      launcher_type: integration?.config?.launcher?.type || 'bubble',
      focus_trap: integration?.config?.focus_trap || false,
      auto_open: integration?.config?.auto_open || false,
      bubble_image_url: integration?.config?.bubble_image || '',
      // The line the old field held is edited as the teaser's message from now on.
      teaser: { ...teaser, message: teaser.message || welcomeMessage },
    });
  }, [
    reset,
    integration?.config?.bubble_image,
    integration?.config?.teaser,
    integration?.config?.launcher?.type,
    integration?.config?.welcome_message,
    integration?.config?.focus_trap,
    integration?.config?.auto_open,
  ]);

  const handleUpdateIntegration = useCallback(() => {
    const {
      welcome_message,
      launcher_image_checkbox,
      focus_trap,
      auto_open,
      launcher_type,
      bubble_image_url,
      teaser,
    } = formMethods.getValues();

    // Validate that image is provided when checkbox is enabled
    if (launcher_image_checkbox && !bubble_image_url) {
      setError('bubble_image_url', {
        type: 'required',
        message: t('integrations.web_launcher.custom_bubble_error'),
      });
      return;
    }

    const savedTeaser = isTeaserEnabled
      ? teaserForSave(teaser, { hasSavedTeaser: !!integration?.config?.teaser })
      : integration?.config?.teaser;
    // Chips and `none` never show the message input, so the line stays in
    // `welcome_message` instead of being dropped unseen.
    const variant = savedTeaser?.variant ?? 'callout';
    const teaserOwnsLine = isTeaserEnabled && (variant === 'callout' || variant === 'composer');

    updateIntegration(
      {
        ...integration,
        config: {
          ...integration?.config,
          welcome_message: teaserOwnsLine ? undefined : welcome_message || undefined,
          teaser: savedTeaser,
          focus_trap,
          auto_open,
          launcher: launcher_type
            ? { ...integration?.config?.launcher, type: launcher_type }
            : undefined,
          bubble_image: launcher_image_checkbox ? bubble_image_url : undefined,
        },
      },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [
    dispatch,
    expanded,
    formMethods,
    integration,
    isTeaserEnabled,
    setError,
    t,
    type,
    updateIntegration,
  ]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(handleUpdateIntegration));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  // for reseting changes
  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [expanded, isDirty, reset, type]);

  const handleAvatarUpdate = useCallback(
    (data) => {
      setValue('bubble_image_url', data?.avatar, { shouldDirty: true });
      if (data?.avatar) {
        clearErrors('bubble_image_url');
      }
    },
    [setValue, clearErrors]
  );

  return (
    <Form {...formMethods}>
      <Accordion
        title={t('integrations.web_launcher.title')}
        subtitle={t('integrations.web_launcher.subtitle')}
        footerText={t('integrations.web_launcher.footer')}
        onSubmit={handleSubmit(handleUpdateIntegration)}
        disabled={!isDirty}
        isLoading={updateStatus === 'pending'}
        expanded={expanded === type}
        handleChange={toggleAccordion(type)}
        readOnly={!canWrite}
        ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TitleSubtitle
              title={t('integrations.web_launcher.title2')}
              subtitle={t('integrations.web_launcher.subtitle2')}
            />
            <div className="flex flex-col gap-2">
              <Controller
                render={({ field: { onChange, value } }) => {
                  return (
                    <LauncherOptions
                      onChange={onChange}
                      value={value}
                      isDisabled={launcherImageActive || !canWrite}
                    />
                  );
                }}
                name="launcher_type"
                control={control}
              />
              <div className="flex flex-col gap-2">
                <Controller
                  render={({ field: { onChange, value } }) => {
                    const handleCheckboxChange = (
                      event: { target: { checked: boolean; type: string } },
                      checked: boolean
                    ) => {
                      onChange(event, checked);
                      // Clear error when checkbox is unchecked
                      if (!checked) {
                        clearErrors('bubble_image_url');
                      }
                    };
                    return (
                      <fs.Checkbox
                        label={t('integrations.web_launcher.custom_bubble')}
                        onChange={handleCheckboxChange}
                        checked={!!value}
                        disabled={
                          launcherType === 'help' || launcherType === 'feedback' || !canWrite
                        }
                      />
                    );
                  }}
                  name="launcher_image_checkbox"
                  control={control}
                />

                {launcherImageActive && (
                  <>
                    <AvatarUpload
                      onUpdate={handleAvatarUpdate}
                      label="bubbleImage"
                      avatar={bubbleImageUrl}
                    />
                    {errors.bubble_image_url && (
                      <span className="text-sm leading-5 text-destructive">
                        {errors.bubble_image_url.message}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <TitleSubtitle
              title={t('integrations.web_launcher.teaser.title')}
              subtitle={t('integrations.web_launcher.teaser.subtitle')}
            />
            {isTeaserEnabled ? (
              <Teaser
                readOnly={!canWrite}
                hasPageTriggers={!!integration?.config?.page_triggers}
                deskId={integration?.desk_id}
                integrationId={integration?.integration_id}
                config={integration?.config}
              />
            ) : (
              <FormField
                control={control}
                name="welcome_message"
                rules={integrationRulesWebWidget.welcomeMessage}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1 space-y-0">
                    <FormLabel className="text-xs leading-3 font-semibold uppercase">
                      {t('integrations.web_launcher.teaser.body')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onBlur={trimOnBlur(field)}
                        placeholder={t('integrations.web_launcher.teaser.placeholder')}
                        data-testid="welcome_message"
                        readOnly={!canWrite}
                      />
                    </FormControl>
                    <FormMessage>
                      {errorMessage({
                        field: errors['welcome_message'],
                        maxLength: integrationRulesWebWidget.welcomeMessage?.maxLength,
                      }) ?? errors['welcome_message']?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />
            )}
          </div>

          <Controller
            render={({ field: { onChange, value } }) => {
              return (
                <fs.Checkbox
                  label={
                    <TitleSubtitle
                      title={t('integrations.web_launcher.focus.title')}
                      subtitle={t('integrations.web_launcher.focus.subtitle')}
                      noGutters
                    />
                  }
                  onChange={onChange}
                  checked={!!value}
                  data-testid="focus_trap"
                  disabled={!canWrite}
                />
              );
            }}
            name="focus_trap"
            control={control}
          />

          <Controller
            render={({ field: { onChange, value } }) => {
              return (
                <fs.Checkbox
                  label={
                    <TitleSubtitle
                      title={t('integrations.web_launcher.auto_open.title')}
                      subtitle={t('integrations.web_launcher.auto_open.subtitle')}
                      noGutters
                    />
                  }
                  onChange={onChange}
                  checked={!!value}
                  disabled={!canWrite}
                />
              );
            }}
            name="auto_open"
            control={control}
          />
        </div>
      </Accordion>
    </Form>
  );
};

export default memo(LauncherAppearance);
